import type { StateCreator } from "zustand";
import { immer } from "zustand/middleware/immer";

interface TelemetryData {
  connectionQualitySeries: Array<{
    timestamp: number;
    value: number;
  }>;
  providerErrors: Array<{
    timestamp: number;
    message: string;
  }>;
}

interface TelemetryActions {
  addConnectionQualityData: (value: number) => void;
  addProviderError: (message: string) => void;
  clearData: () => void;
}

export interface TelemetrySlice {
  telemetry: {
    data: TelemetryData;
    actions: TelemetryActions;
  };
}

const initialTelemetryData: TelemetryData = {
  connectionQualitySeries: [],
  providerErrors: [],
};

const MAX_PROVIDER_ERRORS = 10;

export const createTelemetrySlice: StateCreator<
  TelemetrySlice,
  [],
  [["zustand/immer", never]],
  TelemetrySlice
> = immer(set => ({
  telemetry: {
    data: initialTelemetryData,
    actions: {
      clearData: () => {
        set(state => {
          state.telemetry.data = { ...initialTelemetryData };
        });
      },
      addConnectionQualityData: (value: number) => {
        set(state => {
          state.telemetry.data.connectionQualitySeries.push({
            timestamp: Date.now(),
            value,
          });
        });
      },
      addProviderError: (message: string) => {
        set(state => {
          const errors = state.telemetry.data.providerErrors;
          const lastError = errors[errors.length - 1];

          // Avoid logging duplicated consecutive errors
          if (lastError && lastError.message === message) {
            return;
          }

          state.telemetry.data.providerErrors.push({
            timestamp: Date.now(),
            message,
          });

          if (
            state.telemetry.data.providerErrors.length > MAX_PROVIDER_ERRORS
          ) {
            state.telemetry.data.providerErrors =
              state.telemetry.data.providerErrors.slice(-MAX_PROVIDER_ERRORS);
          }
        });
      },
    },
  },
}));
